import { ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import * as actiontracker from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { PromptuarySidebarService } from 'modules/promptuary/services/sidebar/promptuary-sidebar.service';
import { IAppointment } from 'modules/video-conference/interfaces/appointment.interface';

export class JitsiService {
	private isFirstMute = true;
	private platformOptions = {
		roomName: this.appointment.id,
		userInfo: {
			displayName: this.appointment.professional.name,
		},
		parentNode: this.jitsiElement.nativeElement,
		interfaceConfigOverwrite: {
			JITSI_WATERMARK_LINK: 'cenas',
			MOBILE_APP_PROMO: false,
			TOOLBAR_BUTTONS: [
				'microphone',
				'camera',
				'desktop',
				'fullscreen',
				'fodeviceselection',
				'hangup',
				'settings',
				'videoquality',
				'feedback',
				'stats',
				'shortcuts',
				'tileview',
				'videobackgroundblur',
				'help',
			],
		},
	};

	constructor(
		private jitsiElement: ElementRef<HTMLInputElement>,
		private appointment: IAppointment,
		private stopLoadingParent: () => void,
		private actionTrackerService: ActionTrackerService,
		private promptuarySidebarService: PromptuarySidebarService,
		private router: Router
	) {
		const scriptElem = document.createElement('script');

		scriptElem.type = 'text/javascript';
		scriptElem.async = true;
		scriptElem.src = `https://${this.appointment.serviceDomain}/external_api.js`;
		scriptElem.onload = this.startVideoConference.bind(this);
		scriptElem.onerror = () => {
			console.error('Jitsi: error loading external API');
		};

		document.head.appendChild(scriptElem);
	}

	startVideoConference() {
		if (window.JitsiMeetExternalAPI) {
			const jitsiAPI = new window.JitsiMeetExternalAPI(
				this.appointment.serviceDomain,
				this.platformOptions
			);

			jitsiAPI.executeCommand('subject', this.appointment.title);

			if (this.appointment.professional.avatarUrl) {
				jitsiAPI.addListener('videoConferenceJoined', () => {
					jitsiAPI.executeCommand(
						'avatarUrl',
						this.appointment.professional.avatarUrl
					);
				});
			}
			jitsiAPI.addEventListener('videoConferenceLeft', () => {
				this.trackEvent('Hangup');
				setTimeout(() => {
					this.promptuarySidebarService.openSidebarOrGoToPage(
						this.appointment.contact.id,
						this.appointment.id,
						'video-conference'
					);
				}, 300);
				this.router.navigateByUrl(`/calendar`);
			});

			jitsiAPI.addEventListener('audioMuteStatusChanged', (event) => {
				if (event.muted) {
					this.trackEvent('MuteAudio', { status: event.muted });
				}
			});

			jitsiAPI.addEventListener('videoMuteStatusChanged', (event) => {
				if (event.muted && !this.isFirstMute) {
					this.trackEvent('MuteVideo', { status: event.muted });
				}
				if (event.muted && this.isFirstMute) {
					this.isFirstMute = false;
				}
			});

			jitsiAPI.addEventListener('screenSharingStatusChanged', (event) => {
				if (event.on) {
					this.trackEvent('ShareScreen', { status: event.on });
				}
			});
		}

		this.trackEvent('Joined');
		this.stopLoadingParent();
	}

	trackEvent(
		type: string,
		extraParams?: { status?: string; timeSinceStart?: string }
	) {
		this.actionTrackerService.eventTracker(
			actiontracker.SCOPE_TYPE.Session,
			actiontracker.SUBSCOPE_TYPE.Conference,
			actiontracker.ITEM_TYPE.Professional,
			actiontracker.INTERACTION_TYPE[type],
			{
				sessionId: this.appointment.id,
				jitsiDomain: this.appointment.serviceDomain,
				...extraParams,
			}
		);
	}
}
